"use client"
import { useState } from "react"
import { FileText } from "lucide-react"
import { BasicInformationForm } from "./BasicInformationForm"
import { TravelInsuranceForm } from "./TravelInsuranceForm"
import type { FormType } from "@/shared/types/Forms"

export const VisaApplication = () => {
  const [formType, setFormType] = useState<FormType>("basic")

  return (
    <section className="py-8 sm:py-12 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center border-t-4 border-b-4 border-gray-900 py-4 sm:py-6 mb-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 font-inter">Application Form</h2>
          <div className="text-xs sm:text-sm text-gray-600 uppercase tracking-widest mt-2">Start Your Journey Today</div>
        </div>

        {/* Form Type Selection Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-8">
          <button
            onClick={() => setFormType("basic")}
            className={`relative p-6 rounded-lg border-2 transition-all duration-300 text-left group ${
              formType === "basic"
                ? "border-orange-500 bg-gradient-to-br from-orange-50 to-white shadow-lg scale-105"
                : "border-gray-300 bg-white hover:border-orange-300 hover:shadow-md"
            }`}
          >
            <div className="flex items-start gap-4">
              <div className={`p-3 rounded-full transition-colors ${
                formType === "basic" ? "bg-amber-500" : "bg-gray-200 group-hover:bg-amber-100"
              }`}>
                <FileText className={`w-6 h-6 ${
                  formType === "basic" ? "text-white" : "text-gray-600 group-hover:text-amber-600"
                }`} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Basic Information Form</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Submit your visa application with all necessary personal and travel information
                </p>
              </div>
            </div>
            {formType === "basic" && (
              <div className="absolute top-3 right-3">
                <div className="bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">Selected</div>
              </div>
            )}
          </button>

          <button
            onClick={() => setFormType("travel-insurance")}
            className={`relative p-6 rounded-lg border-2 transition-all duration-300 text-left group ${
              formType === "travel-insurance"
                ? "border-amber-600 bg-gradient-to-br from-amber-50 to-white shadow-lg scale-105"
                : "border-gray-300 bg-white hover:border-amber-400 hover:shadow-md"
            }`}
          >
            <div className="flex items-start gap-4">
              <div className={`p-3 rounded-full transition-colors ${
                formType === "travel-insurance" ? "bg-amber-500" : "bg-gray-200 group-hover:bg-amber-100"
              }`}>
                <FileText className={`w-6 h-6 ${
                  formType === "travel-insurance" ? "text-white" : "text-gray-600 group-hover:text-amber-600"
                }`} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Travel Insurance Form</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Get comprehensive travel insurance coverage for your trip abroad
                </p>
              </div>
            </div>
            {formType === "travel-insurance" && (
              <div className="absolute top-3 right-3">
                <div className="bg-amber-600 text-white text-xs font-bold px-3 py-1 rounded-full">Selected</div>
              </div>
            )}
          </button>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:gap-8">
          <div className="w-full">
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 lg:p-8">
              <div className="border-b-2 border-gray-200 pb-4 sm:pb-6 mb-6 sm:mb-8">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 font-inter">
                      {formType === "basic" ? "Basic Information" : "Travel Insurance"}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-500 mt-1">Complete all required fields</p>
                  </div>
                </div>
              </div>

              {formType === "basic" ? <BasicInformationForm /> : <TravelInsuranceForm />}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
